import { Stack } from '@mui/material';
import { endOfDay } from 'date-fns';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { TenantSecurityConfigurationLuminarySupportAccess } from '@/types/schema';

import {
  GetTenantSecurityConfigurationQuery,
  Operations as TenantSecurityOperations,
} from './graphql/GetTenantSecurityConfiguration.generated';
import { useUpdateTenantSecurityConfigurationMutation } from './graphql/UpdateTenantSecurityConfiguration.generated';

type TenantSecurityConfiguration = NonNullable<
  GetTenantSecurityConfigurationQuery['tenantSecurityConfiguration']
>;

type SupportAccessFormShape = {
  luminarySupportAccess: TenantSecurityConfigurationLuminarySupportAccess;
  luminarySupportAccessEndDate: Date | null;
};

const supportAccessOptions = [
  {
    value: TenantSecurityConfigurationLuminarySupportAccess.NoAccess,
    label: 'No access',
    helpText: 'Luminary support team access is not allowed.',
  },
  {
    value: TenantSecurityConfigurationLuminarySupportAccess.TimeBound,
    label: 'Time-bound access',
    helpText: 'Grant support access through a selected end date.',
  },
  {
    value: TenantSecurityConfigurationLuminarySupportAccess.Indefinite,
    label: 'Indefinite access',
    helpText: 'Grant support access until this policy is changed.',
  },
];

export interface SupportAccessFormProps {
  tenantSecurityConfiguration: TenantSecurityConfiguration;
  readOnly?: boolean;
}

export function SupportAccessForm({
  tenantSecurityConfiguration,
  readOnly = false,
}: SupportAccessFormProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [updateTenantSecurityConfiguration] =
    useUpdateTenantSecurityConfigurationMutation({
      onCompleted: () => {
        showFeedback(FeedbackMessages.formSaveSuccess, { variant: 'success' });
      },
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError, { variant: 'error' });
        reportError('Could not update tenant security configuration', error);
      },
    });

  const formMethods = useForm<SupportAccessFormShape>({
    defaultValues: {
      luminarySupportAccess:
        tenantSecurityConfiguration.luminarySupportAccess ??
        TenantSecurityConfigurationLuminarySupportAccess.NoAccess,
      luminarySupportAccessEndDate:
        tenantSecurityConfiguration.luminarySupportAccessEndDate ?? null,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    reset({
      luminarySupportAccess:
        tenantSecurityConfiguration.luminarySupportAccess ??
        TenantSecurityConfigurationLuminarySupportAccess.NoAccess,
      luminarySupportAccessEndDate:
        tenantSecurityConfiguration.luminarySupportAccessEndDate ?? null,
    });
  }, [reset, tenantSecurityConfiguration]);

  const selectedSupportAccess = useWatch<SupportAccessFormShape>({
    control,
    name: 'luminarySupportAccess',
  });

  async function onSubmit(formData: SupportAccessFormShape) {
    const isTimeBoundAccess =
      formData.luminarySupportAccess ===
      TenantSecurityConfigurationLuminarySupportAccess.TimeBound;

    return updateTenantSecurityConfiguration({
      variables: {
        input: {
          id: tenantSecurityConfiguration.id,
          update: {
            luminarySupportAccess: formData.luminarySupportAccess,
            luminarySupportAccessEndDate:
              isTimeBoundAccess && formData.luminarySupportAccessEndDate
                ? endOfDay(formData.luminarySupportAccessEndDate)
                : undefined,
            clearLuminarySupportAccessEndDate: !isTimeBoundAccess,
          },
        },
      },
      refetchQueries: [
        TenantSecurityOperations.Query.GetTenantSecurityConfiguration,
      ],
      awaitRefetchQueries: true,
    });
  }

  return (
    <FormProvider {...formMethods}>
      <Stack
        component="form"
        spacing={2}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <FormAwareRadioGroup<SupportAccessFormShape>
          label="Luminary support access"
          fieldName={'luminarySupportAccess' as const}
          options={supportAccessOptions}
          control={control}
          columnSpacing={1}
          row={false}
          disabled={readOnly}
        />

        {selectedSupportAccess ===
          TenantSecurityConfigurationLuminarySupportAccess.TimeBound && (
          <FormAwareDatePickerInput<SupportAccessFormShape>
            fieldName={'luminarySupportAccessEndDate' as const}
            label="Support access end date"
            helpText="Required when Time-bound access is selected"
            control={control}
            required
            minDate={new Date()}
            disabled={readOnly}
          />
        )}

        {!readOnly && (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              variant="primary"
              size="sm"
              type="submit"
            >
              Save changes
            </Button>
          </Stack>
        )}
      </Stack>
    </FormProvider>
  );
}
